import { Nullable } from "@data-force/app";
import { notification } from "antd";
import { ArgsProps } from "antd/lib/notification";
import dayjs from "dayjs";
import React from "react";
import {
    getActiveLanguage, getTranslate, Language, TranslateFunction,
} from "react-localize-redux";
import { connect } from "react-redux";
import { Redirect, Route, Switch } from "react-router";
import { ErrorIcon, SuccessIcon } from "../../assets/Icons";
import { ChromeExtensionMissingModal } from "../../components/ClickTrail/ChromeExtensionMissingModal";
import JobTitle from "../../components/common/JobTitle/JobTitle";
import DetailsDrawer from "../../components/DetailsDrawer/DetailsDrawer";
import FilterDrawer from "../../components/FilterDrawer/FilterDrawer";
import HeaderBar from "../../components/HeaderBar/HeaderBar";
import InitiateWorkDialog from "../../components/InitiateWorkDialog/InitiateWorkDialog";
import JobClaimDialog from "../../components/JobClaimDialog/JobClaimDialog";
import NavigationBar from "../../components/NavigationBar/NavigationBar";
import { ActivityNotification } from "../../enums/enums";
import { RoutesMap } from "../../interfaces/common";
import HistoryParams from "../../interfaces/historyParams";
import { ResetActivityNotificationAction } from "../../interfaces/store/actions/job";
import { SetIsMobileAction } from "../../interfaces/store/actions/mobile";
import { ActivityNotificationData } from "../../interfaces/store/actions/payload/job";
import StoreState from "../../interfaces/store/storeState";
import { resetActivityNotification } from "../../store/actions/job";
import { setIsMobile } from "../../store/actions/mobile";
import { formatString, getHistoryParams } from "../../utils/common";
import { Routes } from "../../utils/constants";
import FaqView from "../FaqView/FaqView";
import HomeView from "../HomeView/HomeView";
import JobsView from "../JobsView/JobsView";
import MoreView from "../MoreView/MoreView";
import NoMatchView from "../NoMatchView/NoMatchView";
import QualificationsView from "../QualificationsView/QualificationsView";
import SupportView from "../SupportView/SupportView";
import UnauthorizedView from "../UnauthorizedView/UnauthorizedView";
import "./MainView.scss";

interface DispatchProps {
    setIsMobile(): SetIsMobileAction

    resetActivityNotification(): ResetActivityNotificationAction
}

interface StateProps {
    activeLanguage: Language
    pmUser: boolean
    isMobile: boolean
    notificationData: Nullable<ActivityNotificationData>
    translate: TranslateFunction
}

type Props = StateProps & DispatchProps;

class MainView extends React.Component<Props> {
    private static readonly translatePath: string = "pages.MainView.MainView";

    componentDidMount(): void {
        window.addEventListener("resize", this.props.setIsMobile);
        dayjs.locale(this.props.activeLanguage.code);
    }

    /**
     * Get notification claim message template
     * @param {string} jobId
     * @return {React.ReactNode}
     */
    getClaimMsg(jobId: string): React.ReactNode {
        const {translate}: Props = this.props;
        const jobIdTemplate: string = translate(`${MainView.translatePath}.jobIdFormat.text`) as string;
        return (
            <span>
                {translate(`${MainView.translatePath}.claimNotificationPrefix.text`)}
                <strong>{formatString(jobIdTemplate, jobId)}</strong>
                {translate(`${MainView.translatePath}.claimNotificationSuffix.text`)}
            </span>
        );
    }

    /**
     * Get notification complete message template
     * @param {string} type
     * @param {string} phaseName
     * @return {React.ReactNode}
     */
    getCompleteMsg(type: string, phaseName: string, title: string): React.ReactNode {
        const {translate}: Props = this.props;

        return (
            <span>
                {translate(`${MainView.translatePath}.notificationPrefix.text`)}
                <JobTitle {...{translate, phaseName, title}}/>
                {translate(`${MainView.translatePath}.notificationSuffix.${type}.text`)}
            </span>
        );
    }

    /**
     * Show notification with name and event type data, after notification is shown, we reset notification data.
     * @param {string} phaseName
     * @param {string} type
     * @param {string} jobId
     */
    showNotification({
        phaseName, title, type, jobId,
    }: ActivityNotificationData): void {
        const {translate}: Props = this.props;
        const msg: React.ReactNode = jobId ? this.getClaimMsg(jobId) : this.getCompleteMsg(type, phaseName, title);

        let args: ArgsProps = {
            className: "df-activity-success-notification",
            message: msg,
            icon: <SuccessIcon/>,
        };

        if (type === ActivityNotification.ERROR) {
            args = {
                className: "df-activity-error-notification",
                message: translate(`${MainView.translatePath}.notificationError.text`),
                icon: <ErrorIcon/>,
            };
        }

        notification.open({
            ...args,
            duration: 5,
        });
        this.props.resetActivityNotification();
    }

    render(): React.ReactNode {
        const historyParams: Nullable<HistoryParams> = getHistoryParams();
        const {pmUser, isMobile, notificationData}: Props = this.props;
        const {
            JOBS, FAQ, SUPPORT, UNAUTHORIZED, QUALIFICATIONS, MORE,
        }: RoutesMap = Routes;

        return (
            <div className="df-cmp">
                <NavigationBar/>
                <div className="df-container">
                    <HeaderBar/>
                    <main className="df-content">
                        {notificationData && this.showNotification(notificationData)}
                        <Switch>
                            <Route exact path={`/${historyParams?.idpId}/`}>
                                {pmUser ? <Redirect to={`/${historyParams?.idpId}/${JOBS}`}/> : <HomeView/>}
                            </Route>
                            <Route exact path={`/${historyParams?.idpId}/${JOBS}`}>
                                <JobsView/>
                            </Route>
                            <Route exact path={`/${historyParams?.idpId}/${FAQ}`}>
                                <FaqView/>
                            </Route>
                            <Route exact path={`/${historyParams?.idpId}/${QUALIFICATIONS}`}>
                                <QualificationsView/>
                            </Route>
                            <Route exact path={`/${historyParams?.idpId}/${SUPPORT}`}>
                                <SupportView/>
                            </Route>
                            <Route exact path={`/${historyParams?.idpId}/${UNAUTHORIZED}`}>
                                <UnauthorizedView/>
                            </Route>
                            <Route exact path={`/${historyParams?.idpId}/${MORE}`}>
                                {isMobile ? <MoreView/> : <Redirect to={`/${historyParams?.idpId}/`}/>}
                            </Route>
                            <Route path="*">
                                <NoMatchView/>
                            </Route>
                        </Switch>
                    </main>
                </div>
                <JobClaimDialog/>
                <InitiateWorkDialog/>
                <FilterDrawer/>
                <DetailsDrawer/>
                <ChromeExtensionMissingModal/>
            </div>
        );
    }
}

function mapStateToProps(state: StoreState): StateProps {
    return {
        activeLanguage: getActiveLanguage(state.localize),
        pmUser: state.user.pmUser,
        isMobile: state.mobile.isMobile,
        notificationData: state.job.notificationData,
        translate: getTranslate(state.localize),
    };
}

const mapDispatchToProps: DispatchProps = {
    setIsMobile,
    resetActivityNotification,
};

export default connect(mapStateToProps, mapDispatchToProps)(MainView);
